import { CopyIcon, ResetIcon } from '@radix-ui/react-icons';
import React, { useState, useMemo } from 'react';

import { catchError } from '../../../core/catch-error';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { Project } from '../../../app/entities/project';
import { duplicateProject, unarchiveProject } from '../../../features/projects/store/projects.slice';
import { DuplicateProjectDialog } from '../../../components/dialogs/duplicate-project-dialog';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { Member } from '../../../app/entities/member';

type ProjectMenuProps = {
  project: Project;
};

export const ProjectMenu: React.FC<ProjectMenuProps> = ({ project }) => {
  const [duplicateModalIsOpen, setDuplicateModalIsOpen] = useState(false);
  const currentMember = useCurrentMember();

  const handleProjectDuplicate = async () => {
    try {
      await duplicateProject(project._id);
    } catch (e) {
      catchError(e);
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchiveProject(project._id);
    } catch (e) {
      catchError(e);
    }
  };

  const handleTogglePin = async () => {
    if (currentMember) {
      try {
        await currentMember.toggleProjectPin(project._id);
      } catch (e) {
        catchError(e);
      }
    }
  };

  // Check if user can archive/unarchive
  const canArchive = useMemo(() => {
    if (!currentMember) return false;
    if (currentMember.role === 'guest') return false;
    if (['owner', 'admin'].includes(currentMember.role)) return true;
    // Check if user is project creator
    return project.createdBy === currentMember.userId;
  }, [currentMember, project]);

  const isPinned = currentMember?.preferences?.pinnedProjectIds?.includes(project._id);

  const items = [
    {
      onSelect: () => setDuplicateModalIsOpen(true),
      title: 'Duplicate',
      icon: <CopyIcon />,
    },
    {
      onSelect: handleTogglePin,
      title: isPinned ? 'Unpin' : 'Pin',
      icon: <span>★</span>,
    },
  ];

  // Add unarchive option if user has permission. Archiving is done from project settings.
  if (canArchive && project.archived) {
    items.push({
      onSelect: handleUnarchive,
      title: 'Unarchive',
      icon: <ResetIcon />,
    });
  }

  return (
    <>
      <DropdownMenu items={items} />

      {duplicateModalIsOpen && (
        <DuplicateProjectDialog
          title={'Duplicate project ?'}
          text={`Are you sure you want to duplicate ${project?.name} ?`}
          onCancel={() => setDuplicateModalIsOpen(false)}
          onSubmit={handleProjectDuplicate}
        />
      )}
    </>
  );
};
